import React, { useState, useEffect, useRef, useActionState } from "react";
import { Link } from "react-router-dom";
import { submitContactInfo } from "./submissionFlow";

const DEFAULT_API_BASE = "http://localhost:8080";

const Success: React.FC = () => {
  // LocalStorage-only: taxSessionId is required and authoritative.
  const [formData, setFormData] = useState({ email: "", phone: "" });
  const [errors, setErrors] = useState({ email: "", phone: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionStartedRef = useRef(false);

  const [submissionSucceeded, setSubmissionSucceeded] = useState(false);

  const [taxSessionId, setTaxSessionId] = useState<string | null>(null);
  // personId is optional; the frontend may have stored primary personId under personId_PRIMARY
  const [primaryPersonId, setPrimaryPersonId] = useState<string | null>(null);

  useEffect(() => {
    const storedTaxSessionId = localStorage.getItem("taxSessionId") || null;
    const storedPrimary = localStorage.getItem("personId_PRIMARY") || null;
    if (storedTaxSessionId) setTaxSessionId(storedTaxSessionId);
    if (storedPrimary) setPrimaryPersonId(storedPrimary);
  }, []);

  /* ============================
     Validators
     ============================ */
  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    return (
      digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))
    );
  };

  const API_BASE = (process.env.REACT_APP_API_BASE || DEFAULT_API_BASE).replace(
    /\/$/,
    "",
  );

  // The success page no longer creates persons through the old invalid tax-session persons endpoint.
  // The main submission flow handles person creation and contact submission directly.

  // Unified submission: POST contact and trigger backend processing (email/invoice)
  const submitApplication = async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    if (!taxSessionId) {
      return {
        success: false,
        message: "Missing taxSessionId",
      };
    }

    const numericTaxSessionId = Number(taxSessionId);

    if (!Number.isInteger(numericTaxSessionId) || numericTaxSessionId <= 0) {
      return {
        success: false,
        message: "Invalid taxSessionId",
      };
    }

    const submittedSessionId = localStorage.getItem(
      "contactInfoSubmittedSessionId",
    );

    if (submittedSessionId === String(numericTaxSessionId)) {
      return {
        success: true,
        message:
          "The application and email were already submitted successfully.",
      };
    }

    if (submissionStartedRef.current) {
      return {
        success: false,
        message: "Submission is already in progress.",
      };
    }

    submissionStartedRef.current = true;

    try {
      const result = await submitContactInfo(numericTaxSessionId, {
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
      });

      localStorage.setItem(
        "contactInfoSubmittedSessionId",
        String(numericTaxSessionId),
      );

      setSubmissionSucceeded(true);

      return {
        success: true,
        message:
          typeof result === "string"
            ? result
            : "Application and email submitted successfully.",
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unknown submission error";

      return {
        success: false,
        message,
      };
    } finally {
      submissionStartedRef.current = false;
    }
  };

  /* ============================
     Submit handler
     ============================ */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting || submissionStartedRef.current || submissionSucceeded) {
      return;
    }

    const submittedSessionId = localStorage.getItem(
      "contactInfoSubmittedSessionId",
    );

    if (submittedSessionId && submittedSessionId === taxSessionId) {
      setSubmissionSucceeded(true);

      alert("The application and email were already submitted successfully.");

      return;
    }

    setErrors({
      email: "",
      phone: "",
    });

    const email = formData.email.trim();

    if (!email) {
      setErrors({
        email: "Email is required to send the application.",
        phone: "",
      });

      return;
    }

    if (!validateEmail(email)) {
      setErrors({
        email: "Enter a valid email address.",
        phone: "",
      });

      return;
    }

    if (formData.phone.trim() && !validatePhone(formData.phone)) {
      setErrors({
        email: "",
        phone: "Enter a valid phone number.",
      });

      return;
    }

    if (!taxSessionId) {
      alert(
        "Missing tax session ID. Return to the application page and submit again.",
      );

      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitApplication();

      if (result.success) {
        setSubmissionSucceeded(true);

        alert("Application and email submitted successfully.");
      } else {
        alert(`Submission failed: ${result.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ============================
     UI (unchanged)
     ============================ */
  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: "'Poppins', sans-serif",
        background: "linear-gradient(135deg, #f5f6fa 0%, #e9ecef 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          background: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          padding: "24px 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 32px",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "1.7rem",
              color: "#0a0a23",
              letterSpacing: "1px",
            }}
          >
            DOCEX TAX
          </span>
          <nav>
            <Link
              to="/"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Home
            </Link>
            <Link
              to="/home"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Apply
            </Link>
            <Link
              to="#"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Additional Info
            </Link>
            <Link
              to="#"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Income
            </Link>
            <Link
              to="#"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Deductions
            </Link>
            <Link
              to="#"
              style={{
                margin: "0 18px",
                color: "#0a0a23",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Other
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "60px 20px",
        }}
      >
        <div
          style={{
            maxWidth: 600,
            width: "100%",
            background: "#fff",
            padding: "40px",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          }}
        >
          <h1
            style={{
              textAlign: "center",
              color: "#0a0a23",
              fontSize: "2rem",
              marginBottom: "40px",
            }}
          >
            Your application(s) for filing your tax has been received
            successfully!
          </h1>

          <p
            style={{
              textAlign: "center",
              color: "#666",
              fontSize: "1.1rem",
              marginBottom: "30px",
            }}
          >
            Provide either a valid cell phone number or a valid email address to
            send your invoice
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#444",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `1px solid ${errors.email ? "#dc3545" : "#ddd"}`,
                  borderRadius: "6px",
                  fontSize: "1rem",
                }}
                placeholder="example@email.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span style={{ color: "#dc3545", fontSize: "0.9rem" }}>
                  {errors.email}
                </span>
              )}
            </div>

            <div style={{ marginBottom: "30px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#444",
                }}
              >
                Cell Phone Number
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `1px solid ${errors.phone ? "#dc3545" : "#ddd"}`,
                  borderRadius: "6px",
                  fontSize: "1rem",
                }}
                placeholder="1234567890"
                disabled={isSubmitting}
              />
              {errors.phone && (
                <span style={{ color: "#dc3545", fontSize: "0.9rem" }}>
                  {errors.phone}
                </span>
              )}
            </div>

            {/* If taxSessionId is missing, disable the submit button and show guidance */}
            <div style={{ marginBottom: 12 }}>
              {!taxSessionId && (
                <div style={{ color: "#b02a37", marginBottom: 8 }}>
                  Missing tax session ID. Return to the application page to
                  complete submission before requesting the invoice.
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting || submissionSucceeded || !taxSessionId}
              style={{
                width: "100%",
                padding: "14px",
                backgroundColor: isSubmitting ? "#6ea0ff" : "#007BFF",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "1.1rem",
                fontWeight: "600",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "background-color 0.2s",
              }}
            >
              {submissionSucceeded
                ? "Email Sent"
                : isSubmitting
                  ? "Sending..."
                  : "Send Invoice"}
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer
        style={{
          background: "#f5f6fa",
          padding: "24px 0",
          textAlign: "center",
          color: "#888",
          fontSize: "1rem",
          borderTop: "1px solid #eee",
        }}
      >
        <div>
          &copy; {new Date().getFullYear()} DOCEX Tax. All rights reserved.
        </div>
        <div style={{ marginTop: 8 }}>
          <Link to="/" style={{ color: "#007BFF", margin: "0 10px" }}>
            Home
          </Link>
          <Link to="/home" style={{ color: "#007BFF", margin: "0 10px" }}>
            Apply
          </Link>
          <Link to="#" style={{ color: "#007BFF", margin: "0 10px" }}>
            Additional Info
          </Link>
          <Link to="#" style={{ color: "#007BFF", margin: "0 10px" }}>
            Income
          </Link>
          <Link to="#" style={{ color: "#007BFF", margin: "0 10px" }}>
            Deductions
          </Link>
          <Link to="#" style={{ color: "#007BFF", margin: "0 10px" }}>
            Other
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Success;
